import { type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@ttab/elephant-ui'
import { Newsvalues } from '@/defaults'
import type { DefaultValueOption } from '@/types'
import type { MemoCandidateColumn } from '@/shared/templates/memos'
import type { MemoItem } from '@/shared/templates/memos/lib/renderMemo'
import { selectionAction } from '../lib/selectionAction'
import type { SeededSpan } from '../hooks/useSeededSpan'
import { CandidateList } from './CandidateList'
import { MemoComboBox } from './MemoComboBox'
import { MemoDateFields } from './MemoDateFields'

/** What one candidate column is narrowed by. */
export interface MemoColumnFilter {
  newsvalues: string[]
  sections: string[]
  story: string
}

// Mirrors the Wires filter badges: echo up to two labels, otherwise "N valda".
function selectedFilterLabel(
  selected: DefaultValueOption[],
  selectedLabel: string
): string | undefined {
  if (!selected.length) {
    return undefined
  }

  if (selected.length <= 2) {
    return selected.map((option) => option.label).join(', ')
  }

  return `${selected.length} ${selectedLabel}`
}

/**
 * One column of candidates for the memo: what the index returned for it, and
 * the filters that decide what it asks for.
 */
export const CandidateColumn = ({
  column,
  label,
  items,
  isLoading,
  selectedIds,
  unassignedIds,
  selectedCount,
  onToggle,
  onSelectAll,
  onDeselectAll,
  filter,
  sectionOptions,
  storyOptions,
  showSectionFilter,
  showSelectAll,
  onToggleFilter,
  onSelectStory,
  dates
}: {
  column: MemoCandidateColumn
  label: string
  /**
   * Everything the index returned for this column, picked or not. An item the
   * current filter excludes is gone from here even when it is in the memo -
   * the preview is where the selection is shown in full.
   */
  items: MemoItem[]
  /** Whether the fetch behind the items is still out, spinner and all. */
  isLoading: boolean
  selectedIds: Set<string>
  /** Selected items still waiting on the batch bar's block choice. */
  unassignedIds: Set<string>
  /**
   * How much of the memo came from this column, listed or not - which is not
   * the same as how many of the rows below are ticked, and is what lets the
   * column clear picks the current filter has since hidden.
   */
  selectedCount: number
  onToggle: (item: MemoItem) => void
  onSelectAll: (column: MemoCandidateColumn) => void
  onDeselectAll: (column: MemoCandidateColumn) => void
  filter: MemoColumnFilter
  sectionOptions: DefaultValueOption[]
  storyOptions: DefaultValueOption[]
  showSectionFilter: boolean
  /** Whether the column offers its select-all button; a one-post memo does not. */
  showSelectAll: boolean
  onToggleFilter: (column: MemoCandidateColumn, key: 'newsvalues' | 'sections', value: string) => void
  onSelectStory: (column: MemoCandidateColumn, uuid: string) => void
  /**
   * The column's own days, for a template whose columns have them - see
   * `hasColumnDates` - and what the pickers' "today" button sets.
   */
  dates?: { span: SeededSpan, today: string, invalid?: boolean }
}): JSX.Element => {
  const { t } = useTranslation()

  const storySelection = storyOptions.filter((option) => option.value === filter.story)
  const newsvalueSelection = Newsvalues.filter(
    (option) => filter.newsvalues.includes(option.value)
  )
  const sectionSelection = sectionOptions.filter(
    (option) => filter.sections.includes(option.value)
  )
  const newsvalueLabel = selectedFilterLabel(newsvalueSelection, t('common:misc.selected'))
  const sectionLabel = selectedFilterLabel(sectionSelection, t('common:misc.selected'))

  // A print article carries neither a newsvalue nor a section, so filters for
  // them would narrow a column of print articles down to nothing at all.
  const hasFilters = column !== 'print'

  const unpicked = items.filter((item) => !selectedIds.has(item.id)).length
  const selection = selectionAction(unpicked, selectedCount)

  return (
    <div
      role='group'
      aria-label={label}
      className='flex flex-col gap-2 min-w-0'
    >
      <div className='flex items-center justify-between gap-2'>
        <h4 className='text-sm font-medium text-muted-foreground truncate'>
          {label}
        </h4>

        {/*
          Taking or clearing the column sits beside the name of the thing it
          acts on. One button rather than two: it offers the rest of the
          column while there is any of it left to take, and only clears once
          there is not, so the label is always what a click would do.
        */}
        {showSelectAll && (
          <Button
            variant='outline'
            size='xs'
            className='shrink-0'
            disabled={selection.disabled}
            onClick={() => {
              if (selection.action === 'select') {
                onSelectAll(column)

                return
              }

              onDeselectAll(column)
            }}
          >
            {selection.action === 'select'
              ? t('memo:actions.selectAll')
              : t('memo:actions.deselectAll')}
          </Button>
        )}
      </div>

      {!!dates && (
        <MemoDateFields span={dates.span} today={dates.today} invalid={dates.invalid} />
      )}

      {/* A column with nothing to filter by still keeps the space a filter row
          takes, so that the lists in every column start at the same height. */}
      {!hasFilters && <div className='h-8' aria-hidden='true' />}

      {hasFilters && (
        <div className='flex flex-wrap items-center gap-2 min-h-8'>
          <MemoComboBox
            options={Newsvalues}
            selectedOptions={newsvalueSelection}
            placeholder={t('memo:placeholders.newsvalue')}
            onSelect={(option) => onToggleFilter(column, 'newsvalues', option.value)}
          >
            {newsvalueLabel && <span className='truncate'>{newsvalueLabel}</span>}
          </MemoComboBox>

          {showSectionFilter && (
            <MemoComboBox
              sortOrder='label'
              options={sectionOptions}
              selectedOptions={sectionSelection}
              placeholder={t('memo:placeholders.filterSection')}
              onSelect={(option) => onToggleFilter(column, 'sections', option.value)}
            >
              {sectionLabel && <span className='truncate'>{sectionLabel}</span>}
            </MemoComboBox>
          )}

          {/* No stories in the datastore means nothing to pick from, so the
              filter is left out rather than offered as an empty list. */}
          {!!storyOptions.length && (
            <MemoComboBox
              sortOrder='label'
              options={storyOptions}
              selectedOptions={storySelection}
              placeholder={t('memo:placeholders.filterStory')}
              onSelect={(option) => onSelectStory(column, option.value)}
            >
              {!!storySelection.length && (
                <span className='truncate'>{storySelection[0].label}</span>
              )}
            </MemoComboBox>
          )}
        </div>
      )}

      <CandidateList
        items={items}
        isLoading={isLoading}
        selectedIds={selectedIds}
        unassignedIds={unassignedIds}
        onToggle={onToggle}
      />
    </div>
  )
}
